package dev.lingvo.shared.screenshots.doc

import dev.lingvo.shared.StorePlatform
import kotlinx.serialization.Serializable

@Serializable
enum class Severity { ERROR, WARNING }

/** Every reason a document is refused (ERROR) or annotated (WARNING), SPEC-B 1.3 and 1.4. */
@Serializable
enum class DesignIssueCode(val severity: Severity) {
    // Pre-check, before any parser sees the text.
    PAYLOAD_TOO_LARGE(Severity.ERROR),
    TOO_DEEP(Severity.ERROR),
    TOO_MANY_TOKENS(Severity.ERROR),
    STRING_TOO_LONG(Severity.ERROR),

    // Parsing and decoding.
    MALFORMED(Severity.ERROR),
    MISSING_VERSION(Severity.ERROR),
    UNSUPPORTED_VERSION(Severity.ERROR),
    UNKNOWN_FIELD(Severity.ERROR),
    UNKNOWN_TYPE(Severity.ERROR),
    NOT_FINITE(Severity.ERROR),

    // Structure.
    TOO_MANY_BOARDS(Severity.ERROR),
    DUPLICATE_DEVICE_CLASS(Severity.ERROR),
    TOO_MANY_PAGES(Severity.ERROR),
    TOO_MANY_LAYERS(Severity.ERROR),
    GROUP_TOO_DEEP(Severity.ERROR),
    BAD_ID(Severity.ERROR),
    DUPLICATE_ID(Severity.ERROR),
    BACKGROUND_NOT_FIRST(Severity.ERROR),
    SLOT_REPEATED(Severity.ERROR),
    BAD_SLOT_REF(Severity.ERROR),
    CANVAS_SIZE_MISMATCH(Severity.ERROR),

    // Fields.
    BAD_TEXT(Severity.ERROR),
    BAD_KEY(Severity.ERROR),
    BAD_LOCALE(Severity.ERROR),
    UNKNOWN_LAYER(Severity.ERROR),
    BAD_COLOR(Severity.ERROR),
    UNKNOWN_FONT(Severity.ERROR),
    BAD_ASSET_ID(Severity.ERROR),
    OUT_OF_RANGE(Severity.ERROR),
    FLOOR_ABOVE_MAX(Severity.ERROR),
    BAD_STOPS(Severity.ERROR),

    // Warnings: stored and shown, never refuse.
    TEXT_CROSSES_SEAM(Severity.WARNING),
    LITERAL_TEXT(Severity.WARNING),
    CAPTION_HAS_PLACEHOLDER(Severity.WARNING),
    LOW_CONTRAST(Severity.WARNING),
    OUTSIDE_CANVAS(Severity.WARNING),
    TEXT_OUTSIDE_SAFE_AREA(Severity.WARNING),
}

/** One finding. [path] is a JSON Pointer into the document, e.g. `/boards/0/pages/2/layers/5/base/box/w`. */
@Serializable
data class DesignIssue(
    val code: DesignIssueCode,
    val path: String,
    val message: String,
    val severity: Severity = code.severity,
)

/**
 * The device-class table as the engine needs it: which ids exist on a platform, their one stored spelling, their
 * store sizes and how many screenshots a store shows per class.
 *
 * Track A owns the table (`DeviceClass`, `ShotSpec`); until it lands on this branch the engine codes against this
 * interface, and binding it to A's table is SPEC-B task B10. Ids are compared only after [canonicalId], so
 * `IPHONE_69`, `Iphone_69` and an alias of the same class are one class, and a document stores exactly the
 * canonical spelling (lowercase).
 */
interface DeviceClassRule {
    /**
     * The canonical id of [raw] on [platform] (case-insensitive, aliases accepted), or null when no such class
     * exists on that platform.
     */
    fun canonicalId(platform: StorePlatform, raw: String): String?

    /** Whether [width] x [height] is a store size for the class [canonicalId]. */
    fun allows(canonicalId: String, width: Int, height: Int): Boolean

    /** How many screenshots the store takes for the class (Play: 8, App Store: 10). */
    fun maxSlots(canonicalId: String): Int
}

/**
 * Whether a font family name may appear in a document. The default is [DesignFonts.V1], the families SPEC-B 7.1,
 * 8.1 and 8.2 name; B8's `FontCatalog` replaces it and must cover exactly those names.
 */
fun interface FontNameRule {
    fun knows(family: String): Boolean
}

/**
 * The hard limits of SPEC-B 1.4, one set for the server, the editor and the tests (SPEC-A 1.5 delegates here).
 *
 * The byte budget is the binding one: the server stores [DesignCodec.encode] (defaults omitted) under a database
 * check of [MAX_CANONICAL_BYTES]. The count limits are set so a document at every board, page and layer limit
 * together, made of ordinary layers, still fits that budget (`DesignBudgetTest`); per-locale overrides and long
 * strings are bounded by the byte check itself, not by counts.
 */
object DesignLimits {
    /** Raw request text: the server's request cap, so the pre-check refuses nothing a request could carry. */
    const val MAX_RAW_BYTES = 256 * 1024

    /** The canonical encoding, which is what is stored and hashed. */
    const val MAX_CANONICAL_BYTES = 245_760
    const val MAX_DEPTH = 32

    /** JSON values (objects, arrays and scalars; keys are not values), in the raw text and the parsed tree. */
    const val MAX_VALUES = 20_000
    const val MAX_ARRAY_ITEMS = 2_000

    /** Code points, the one unit every string limit uses. */
    const val MAX_STRING_CHARS = 4_096

    const val MAX_BOARDS = 8
    const val MAX_PAGES_PER_BOARD = 10
    const val MAX_SLOTS_PER_PAGE = 10
    const val MAX_SLOTS_PER_BOARD = 10
    const val MAX_LAYERS_PER_PAGE = 64
    const val MAX_LAYERS_PER_DOCUMENT = 600
    const val MAX_GROUP_DEPTH = 4

    const val MAX_LAYER_NAME = 80
    const val MAX_BRAND_NAME = 60
    const val MAX_VOICE_NOTES = 500
    const val MAX_AVOID_ITEMS = 20
    const val MAX_AVOID_ITEM = 40
    const val MAX_LITERAL = 300

    const val MAX_OVERRIDE_LOCALES = 80
    const val MAX_PATCHES_PER_LOCALE = 64
    const val MAX_ICON_PALETTE = 5
    const val MAX_SLOT_ACCENTS = 10

    /** Strip coordinates: a 10-slot Mac strip is 28,800 wide and an 8-slot Play tablet strip up to 61,440. */
    const val COORD_LIMIT = 70_000.0

    /** Below this a box, a group or a dash is degenerate: fit, hit-test and dash maths stop meaning anything. */
    const val MIN_EXTENT = 1.0

    /** Non-zero magnitudes below this are refused: they are noise, and subnormals cannot be spelled one way. */
    const val MIN_MAGNITUDE = 1e-6
    const val ROTATION_LIMIT = 360.0
    const val MAX_TILT = 30.0
    const val MIN_CAPTION_SHARE = 0.05
    const val MAX_CAPTION_SHARE = 0.6
    const val MIN_DEVICE_SCALE = 0.1
    const val MAX_DEVICE_SCALE = 4.0
    const val MIN_FONT_SIZE = 4.0
    const val MAX_FONT_SIZE = 1_000.0
    const val MIN_LINE_HEIGHT = 0.6
    const val MAX_LINE_HEIGHT = 3.0
    const val MIN_LETTER_SPACING = -0.2
    const val MAX_LETTER_SPACING = 0.5
    const val MAX_LINES = 6

    /**
     * Blur and shadow offsets are canvas pixels. No layer transform scales (boxes translate, rotate and flip),
     * so at export they are device pixels too and this bounds the cost of the blur directly.
     */
    const val MAX_BLUR = 400.0
    const val MAX_SHADOW_OFFSET = 500.0
    const val MAX_STROKE_WIDTH = 200.0

    /** Each dash length is at least [MIN_EXTENT] and the pattern at least this long. */
    const val MIN_DASH_PATTERN = 2.0
    const val MIN_CROP = 0.01
    const val MIN_STOPS = 2
    const val MAX_STOPS = 8
    const val MAX_BLOBS = 6
    const val MIN_CURVE_POINTS = 2
    const val MAX_CURVE_POINTS = 16
    const val MAX_DASH = 8
    const val MAX_GRAIN = 0.15
    const val MAX_PLAN_OFFSET = 9

    /** Ids of boards, pages and layers. */
    val ID_REGEX = Regex("^[A-Za-z0-9_-]{1,40}$")

    /** Slot keys match V72 so a saved plan can be referenced without truncation. */
    val SLOT_KEY_REGEX = Regex("^[a-z0-9][a-z0-9_-]{0,47}$")

    /** Asset ids: never a URL, so the server has nothing to fetch (SPEC-B 9.6 / ADR on SSRF). */
    val ASSET_ID_REGEX = Regex("^[a-z0-9][a-z0-9-]{0,63}$")

    /** The stored spelling of a device class id: lowercase, as [DeviceClassRule.canonicalId] returns it. */
    val DEVICE_CLASS_REGEX = Regex("^[a-z0-9._-]{1,64}$")

    /** Template ids (SPEC-B 7): lowercase words joined by hyphens. */
    val TEMPLATE_ID_REGEX = Regex("^[a-z0-9][a-z0-9-]{0,39}$")
}
